import { api } from "../utils/api";
import isClassDiaryDemo from "../demo/isClassDiaryDemo";
import { DEMO_TURMA_DISC } from "../demo/buildDemoOccurrences";
import {
  buildDemoIndReportConcepts,
  buildDemoIndReportData,
  buildDemoIndReportList,
  buildDemoIndReportPeriods
} from "../demo/buildDemoIndividualReport";
import ClassService from "./ClassService";

function appendSemConceito(concepts) {
  const list = Array.isArray(concepts) ? [...concepts] : [];
  list.push({ label: "Sem conceito", color: "#FFFFFF" });
  return list;
}

const getReportData = async (idTurmaDisc, period) => {
  if (isClassDiaryDemo()) {
    return buildDemoIndReportData(idTurmaDisc, period);
  }
  try {
    const res = await api.get("ClassDiaryV1/individual-report", {
      params: { idTurmaDisc, period }
    });
    if (
      res.data &&
      typeof res.data === "object" &&
      Array.isArray(res.data.students) &&
      Array.isArray(res.data.axis)
    ) {
      return res.data;
    }
  } catch {
    /* fallback abaixo para turma demo */
  }
  if (String(idTurmaDisc) === String(DEMO_TURMA_DISC)) {
    return buildDemoIndReportData(idTurmaDisc, period);
  }
  return { axis: [], students: [] };
};

/**
 * Busca a paleta de conceitos aplicável ao relatório.
 *
 * `idTurmaDisc` é opcional: quando informado, permite que o backend (ou o
 * fixture demo) devolva paletas diferentes por disciplina (ex.: o template
 * "Relatório de Habilidades" usa C/CO/AC/NA).
 */
const getConcepts = async idTurmaDisc => {
  if (isClassDiaryDemo()) {
    return appendSemConceito(buildDemoIndReportConcepts(idTurmaDisc));
  }
  try {
    const res = await api.get("ClassDiaryV1/individual-report/concepts", {
      params: idTurmaDisc ? { idTurmaDisc } : undefined
    });
    const concepts = Array.isArray(res.data) ? res.data : [];
    return appendSemConceito(concepts);
  } catch {
    return appendSemConceito(buildDemoIndReportConcepts(idTurmaDisc));
  }
};

const getPeriods = async idTurmaDisc => {
  if (isClassDiaryDemo()) {
    return buildDemoIndReportPeriods(idTurmaDisc);
  }
  try {
    const res = await api.get("ClassDiaryV1/individual-report/periods", {
      params: { idTurmaDisc }
    });
    if (Array.isArray(res.data) && res.data.length) {
      return res.data;
    }
  } catch {
    /* fallback */
  }
  if (String(idTurmaDisc) === String(DEMO_TURMA_DISC)) {
    return buildDemoIndReportPeriods(idTurmaDisc);
  }
  return [];
};

/**
 * Disciplinas/modelos do RI (WAYBEE.033) — não usa a lista geral de disciplinas da turma.
 * Cada item = um IDTURMADISC com template de conceito (ex. "Relatório de Habilidades").
 */
const getReportCourses = async classId => {
  if (isClassDiaryDemo()) {
    const list = buildDemoIndReportList(classId) || [];
    const byTd = new Map();
    list.forEach(row => {
      const idTd = row.discipline?.idTurmaDisc;
      if (!idTd || byTd.has(String(idTd))) return;
      const modelTitle = row.modelTitle || row.discipline?.modelTitle;
      const discName = row.discipline?.name || "";
      byTd.set(String(idTd), {
        id: row.discipline?.id || idTd,
        name: modelTitle || discName || String(idTd),
        idTurmaDisc: idTd,
        modelTitle,
        discName
      });
    });
    return Array.from(byTd.values());
  }

  try {
    const res = await api.get("ClassDiaryV1/individual-report/templates", {
      params: { classId }
    });
    const rows = Array.isArray(res.data) ? res.data : [];
    const byTd = new Map();

    rows.forEach(row => {
      const idTd = String(row.idTurmaDisc || row.discipline?.idTurmaDisc || "");
      if (!idTd || byTd.has(idTd)) return;
      const modelTitle = row.modelTitle || row.discipline?.modelTitle || "";
      const discName = row.discipline?.name || "";
      byTd.set(idTd, {
        id: row.discipline?.id || idTd,
        // Preferência: título do modelo RI (WAYBEE.033 / SGRUPOCONCEITO)
        name: modelTitle || discName || idTd,
        idTurmaDisc: idTd,
        modelTitle: modelTitle || null,
        discName: discName || null,
        idGroup: row.idGroup || row.discipline?.idGroup || null
      });
    });

    // Homônimos: "Relatório Individual — Matemática"
    const titleCount = {};
    byTd.forEach(c => {
      if (c.modelTitle) titleCount[c.modelTitle] = (titleCount[c.modelTitle] || 0) + 1;
    });

    return Array.from(byTd.values()).map(c => {
      if (c.modelTitle && titleCount[c.modelTitle] > 1 && c.discName) {
        return { ...c, name: `${c.modelTitle} — ${c.discName}` };
      }
      return c;
    });
  } catch (e) {
    return [];
  }
};

const save = async data => {
  if (isClassDiaryDemo()) {
    return { success: true };
  }
  const res = await api.post("ClassDiaryV1/individual-report", data);
  return res.data;
};

/**
 * Lista templates RI da turma (disciplina × período com modelo de conceito).
 * Prefere `/individual-report/templates` (WAYBEE.033); fallback no cartesiano antigo.
 */
const listReports = async classId => {
  if (isClassDiaryDemo()) {
    return buildDemoIndReportList(classId);
  }

  try {
    const res = await api.get("ClassDiaryV1/individual-report/templates", {
      params: { classId }
    });
    if (Array.isArray(res.data) && res.data.length) {
      return res.data.map(row => ({
        reportKey: row.reportKey,
        classId,
        discipline: row.discipline,
        period: row.period,
        modelTitle: row.modelTitle,
        studentsCount: null,
        indexesCount: null,
        filledPercent: null,
        updatedAt: null
      }));
    }
  } catch {
    /* fallback abaixo */
  }

  const disciplines = await ClassService.getDisciplines(classId);
  if (!Array.isArray(disciplines) || disciplines.length === 0) return [];

  const periodsByDiscipline = await Promise.all(
    disciplines.map(d => getPeriods(d.idTurmaDisc))
  );

  return disciplines.reduce((rows, discipline, i) => {
    const periods = periodsByDiscipline[i] || [];
    periods.forEach(period => {
      rows.push({
        reportKey: `${discipline.idTurmaDisc}::${period.id}`,
        classId,
        discipline,
        period,
        studentsCount: null,
        indexesCount: null,
        filledPercent: null,
        updatedAt: null
      });
    });
    return rows;
  }, []);
};

export default {
  getReportData,
  getConcepts,
  getPeriods,
  getReportCourses,
  listReports,
  save
};
